import React, { useState, useEffect } from 'react';
import { 
  Card, 
  CardContent, 
  Typography, 
  Button, 
  TextField, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  List, 
  ListItem, 
  ListItemText, 
  ListItemSecondaryAction, 
  Alert,
  Box,
  Divider,
  Stack
} from '@mui/material';
import { createSession, editSession, deleteSession, listSessions } from './api';

interface Session {
  session: string;
  createdAt: string;
}

interface SessionManagerProps {
  onSessionSelect: (sessionName: string) => void;
}

const SessionManager: React.FC<SessionManagerProps> = ({ onSessionSelect }) => {
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Create session state
  const [createDialog, setCreateDialog] = useState(false);
  const [newSessionName, setNewSessionName] = useState('');
  const [newSessionPassword, setNewSessionPassword] = useState('');

  // Edit session state
  const [editDialog, setEditDialog] = useState(false);
  const [editSessionName, setEditSessionName] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');

  // Delete session state
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleteSessionName, setDeleteSessionName] = useState('');
  const [deletePassword, setDeletePassword] = useState('');

  useEffect(() => {
    loadSessions();
  }, []);

  const loadSessions = async () => {
    try {
      setLoading(true);
      const result = await listSessions();
      if (Array.isArray(result)) {
        setSessions(result);
      } else {
        setError('Failed to load sessions');
      }
    } catch (err) {
      setError('Failed to load sessions');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSession = async () => {
    try {
      setError('');
      const result = await createSession(newSessionName, newSessionPassword);
      if (result.error) {
        setError(result.error);
      } else {
        setSuccess('Session created successfully');
        setCreateDialog(false);
        setNewSessionName('');
        setNewSessionPassword('');
        loadSessions();
      }
    } catch (err) {
      setError('Failed to create session');
    }
  };

  const handleEditSession = async () => {
    try {
      setError('');
      const result = await editSession(editSessionName, currentPassword, newPassword);
      if (result.error) {
        setError(result.error);
      } else {
        setSuccess('Session password updated successfully');
        setEditDialog(false);
        setEditSessionName('');
        setCurrentPassword('');
        setNewPassword('');
      }
    } catch (err) {
      setError('Failed to update session');
    }
  };

  const handleDeleteSession = async () => {
    try {
      setError('');
      const result = await deleteSession(deleteSessionName, deletePassword);
      if (result.error) {
        setError(result.error);
      } else {
        setSuccess('Session deleted successfully');
        setDeleteDialog(false);
        setDeleteSessionName('');
        setDeletePassword('');
        loadSessions();
      }
    } catch (err) {
      setError('Failed to delete session');
    }
  };

  const handleAccessSession = (sessionName: string) => {
    // Directly navigate to voting page without password verification
    onSessionSelect(sessionName);
  };

  const openEditDialog = (sessionName: string) => {
    setEditSessionName(sessionName);
    setEditDialog(true);
  };

  const openDeleteDialog = (sessionName: string) => {
    setDeleteSessionName(sessionName);
    setDeleteDialog(true);
  };

  return (
    <div style={{ 
      maxWidth: 800, 
      margin: '0 auto', 
      padding: 20,
      minHeight: '100vh',
      backgroundImage: 'url(/tile_background.png)',
      backgroundRepeat: 'repeat',
      backgroundSize: 'auto'
    }}>
      <Typography variant="h4" gutterBottom align="center">
        Toastmasters Voting Sessions
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
        <Button 
          variant="contained" 
          onClick={() => setCreateDialog(true)}
          sx={{ mr: 2 }}
        >
          Create New Session
        </Button>
      </Box>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Existing Sessions
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          {loading ? (
            <Typography>Loading sessions...</Typography>
          ) : sessions.length === 0 ? (
            <Typography color="textSecondary">
              No sessions found. Create your first session above.
            </Typography>
          ) : (
            <List>
              {sessions.map((session, index) => (
                <ListItem key={index} divider>
                  <ListItemText
                    primary={session.session}
                    secondary={`Created: ${new Date(session.createdAt).toLocaleDateString()}`}
                  />
                  <ListItemSecondaryAction>
                    <Stack direction="row" spacing={1}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleAccessSession(session.session)}
                      >
                        Access
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => openEditDialog(session.session)}
                      >
                        Edit
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        onClick={() => openDeleteDialog(session.session)}
                      >
                        Delete
                      </Button>
                    </Stack>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </CardContent>
      </Card>

      {/* Create Session Dialog */}
      <Dialog open={createDialog} onClose={() => setCreateDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Session</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Session Name"
            fullWidth
            variant="outlined"
            value={newSessionName}
            onChange={(e) => setNewSessionName(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Password"
            type="password"
            fullWidth
            variant="outlined"
            value={newSessionPassword}
            onChange={(e) => setNewSessionPassword(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleCreateSession}
            disabled={!newSessionName.trim() || !newSessionPassword.trim()}
            variant="contained"
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Session Dialog */}
      <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Session Password</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="Session Name"
            fullWidth
            variant="outlined"
            value={editSessionName}
            disabled
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Current Password"
            type="password"
            fullWidth
            variant="outlined"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="New Password"
            type="password"
            fullWidth
            variant="outlined"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleEditSession}
            disabled={!currentPassword.trim() || !newPassword.trim()}
            variant="contained"
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Session Dialog */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Delete Session</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to delete session "{deleteSessionName}"? This action cannot be undone.
          </Typography>
          <TextField
            margin="dense"
            label="Password"
            type="password"
            fullWidth
            variant="outlined"
            value={deletePassword}
            onChange={(e) => setDeletePassword(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleDeleteSession}
            disabled={!deletePassword.trim()}
            variant="contained"
            color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default SessionManager;
